/**
 * Admin approval queue (closed pilot) — `/admin`.
 *
 * Client-gated on `me.isAdmin` (a UX affordance only): a non-admin who
 * navigates here is redirected back to the feed. The server independently
 * re-checks the caller's admin allowlist on every `/admin/*` request — this
 * page is not itself an authorization boundary, only a hint of one.
 *
 * Lists users awaiting approval and lets the admin approve them one at a
 * time; an approved row is optimistically removed from the list (no full
 * refetch needed).
 */

import type { PendingUser } from "@tjommi/shared";
import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { UnauthorizedError, approveUser, fetchPendingUsers } from "../api";
import { AllUsersSection } from "../components/AllUsersSection";
import { formatDateTime as formatCreatedAt } from "../lib/formatDate";

type QueueState =
  | { status: "loading" }
  | { status: "ok"; users: PendingUser[] }
  | { status: "unauthorized" }
  | { status: "error"; message: string };

export function AdminPage({
  isAdmin,
  accessToken,
  onSessionExpired,
}: {
  isAdmin: boolean;
  accessToken: string;
  onSessionExpired: () => void;
}): React.JSX.Element {
  const [queue, setQueue] = useState<QueueState>({ status: "loading" });
  const [approvingSubject, setApprovingSubject] = useState<string | null>(null);
  const [approveError, setApproveError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return; // redirected below — no need to fetch.
    let ignore = false;
    setQueue({ status: "loading" });

    fetchPendingUsers(accessToken)
      .then((users) => {
        if (!ignore) setQueue({ status: "ok", users });
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (err instanceof UnauthorizedError) {
          setQueue({ status: "unauthorized" });
        } else {
          setQueue({
            status: "error",
            message: err instanceof Error ? err.message : "Ukjent feil",
          });
        }
      });

    return () => {
      ignore = true;
    };
  }, [isAdmin, accessToken]);

  // A non-admin never sees this screen — the API would 403 anyway, but this
  // avoids a confusing empty/forbidden state in the UI.
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  function handleApprove(subject: string): void {
    setApproveError(null);
    setApprovingSubject(subject);
    approveUser(subject, accessToken)
      .then(() => {
        setQueue((prev) =>
          prev.status === "ok"
            ? { status: "ok", users: prev.users.filter((u) => u.subject !== subject) }
            : prev,
        );
      })
      .catch((err: unknown) => {
        if (err instanceof UnauthorizedError) {
          onSessionExpired();
          return;
        }
        setApproveError(err instanceof Error ? err.message : "Kunne ikke godkjenne brukeren.");
      })
      .finally(() => setApprovingSubject(null));
  }

  return (
    <main className="app-main">
      <Link to="/" className="link-btn" data-testid="admin-back">
        ← Tilbake til oppskrifter
      </Link>
      <div className="feed-h" style={{ marginTop: "16px" }}>
        <h1>Ventende brukere</h1>
        {queue.status === "ok" && (
          <span className="feed-count" data-testid="pending-count">
            {queue.users.length} {queue.users.length === 1 ? "venter" : "venter"}
          </span>
        )}
      </div>

      {queue.status === "loading" && (
        <div className="feed-state" data-testid="admin-loading">
          <p>Laster …</p>
        </div>
      )}

      {queue.status === "unauthorized" && (
        <div className="feed-state feed-error" data-testid="admin-unauthorized">
          <h2>Økten er utløpt</h2>
          <p>Du må logge inn på nytt.</p>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: "16px" }}
            data-testid="admin-relogin"
            onClick={onSessionExpired}
          >
            Logg inn igjen
          </button>
        </div>
      )}

      {queue.status === "error" && (
        <div className="feed-state feed-error" data-testid="admin-error">
          <h2>Kunne ikke laste ventende brukere</h2>
          <p>{queue.message}</p>
        </div>
      )}

      {queue.status === "ok" && queue.users.length === 0 && (
        <div className="feed-state feed-empty-state" data-testid="admin-empty">
          <h2>Ingen ventende brukere</h2>
          <p>Alle registrerte brukere er godkjent.</p>
        </div>
      )}

      {queue.status === "ok" && queue.users.length > 0 && (
        <>
          {approveError && (
            <p className="feed-error" data-testid="admin-approve-error" style={{ padding: "12px" }}>
              {approveError}
            </p>
          )}
          <ul className="admin-queue" data-testid="admin-queue">
            {queue.users.map((user) => {
              const label = user.displayName ?? user.email ?? user.subject;
              const isApproving = approvingSubject === user.subject;
              return (
                <li
                  key={user.subject}
                  className="admin-row card card-pad"
                  data-testid={`admin-row-${user.subject}`}
                >
                  <div className="admin-row-body">
                    <div className="admin-row-name" data-testid="admin-row-name">
                      {label}
                    </div>
                    {user.email && (
                      <div className="admin-row-email" data-testid="admin-row-email">
                        {user.email}
                      </div>
                    )}
                    <div className="admin-row-created" data-testid="admin-row-created">
                      Registrert {formatCreatedAt(user.createdAt)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-testid={`approve-${user.subject}`}
                    disabled={isApproving}
                    onClick={() => handleApprove(user.subject)}
                  >
                    {isApproving ? "Godkjenner …" : "Godkjenn"}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <AllUsersSection accessToken={accessToken} onSessionExpired={onSessionExpired} />
    </main>
  );
}
